import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingScreen from '../../components/LoadingScreen';
import ClassInputForm from '../../components/ClassInputForm';
import supabase from '../../utils/supabaseClient';

// Device class display names
const classNames = {
  'class_1': 'Class 1',
  'class_2': 'Class 2',
  'class_3': 'Class 3'
};

const ClassDetailsPage = () => {
  const { id, deviceClass } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({});
  const [productData, setProductData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Get properly formatted class title
  const className = classNames[deviceClass] || deviceClass;

  // Load the product
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setIsLoading(true);
        
        const { data, error: fetchError } = await supabase
          .from('products')
          .select('*')
          .eq('id', id)
          .single();
          
        if (fetchError) throw fetchError;
        
        setProductData(data);
        
        // Initialize form data with any existing values
        const initialData = Object.keys(data).reduce((acc, key) => {
          // Convert snake_case to camelCase for the form
          const camelKey = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
          acc[camelKey] = data[key];
          return acc;
        }, {});
        
        setFormData(initialData);
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Failed to load product details. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (user && id) {
      fetchProduct();
    }
  }, [user, id]);

  // Convert camelCase form data keys to snake_case for API
  const convertToSnakeCase = (obj) => {
    const result = {};
    Object.keys(obj).forEach(key => {
      // Convert camelCase to snake_case
      const snakeKey = key.replace(/([A-Z])/g, "_$1").toLowerCase();
      
      if (Array.isArray(obj[key])) {
        // If it's an array (like for checkboxes), convert to JSON string
        result[snakeKey] = JSON.stringify(obj[key]);
      } else {
        result[snakeKey] = obj[key];
      }
    });
    return result;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Convert formData keys to snake_case for API
      const snakeCaseFormData = convertToSnakeCase(formData);
      
      // Filter out fields that don't exist in the original product data
      const existingFields = Object.keys(productData);
      const filteredData = Object.keys(snakeCaseFormData)
        .filter(key => existingFields.includes(key))
        .reduce((obj, key) => {
          obj[key] = snakeCaseFormData[key];
          return obj;
        }, {});
      
      // Update the product with class-specific details
      const { error: updateError } = await supabase
        .from('products')
        .update(filteredData)
        .eq('id', id);
      
      if (updateError) throw updateError;
      
      // Navigate to the product details page
      navigate(`/products/${id}`);
      
    } catch (err) {
      console.error('Error updating product:', err);
      setError('Failed to update product details. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || isLoading) {
    return <LoadingScreen message="Loading product details..." />;
  }

  if (!productData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
          <p>Product not found. Please check the URL and try again.</p>
        </div>
        <Link to="/products" className="btn-primary">
          Back to Products
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Link to="/products" className="text-primary-600 hover:text-primary-700 flex items-center">
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Back to Products
        </Link>
      </div>
      
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{productData.name} - {className} Details</h1>
        <p className="text-gray-600">Please enter the class-specific details for your {className} medical device.</p>
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
          <p>{error}</p>
        </div>
      )}
      
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <form onSubmit={handleSubmit}>
          <div className="p-6 border-b">
            <ClassInputForm
              deviceClass={deviceClass.charAt(0).toUpperCase() + deviceClass.slice(1)}
              formData={formData}
              setFormData={setFormData}
            />
          </div>
          
          <div className="bg-gray-50 px-6 py-4 flex justify-end">
            <button
              type="button"
              onClick={() => navigate('/products')}
              className="btn-secondary mr-4"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Details'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClassDetailsPage; 